import { AlertTriangle, CheckCircle2, Info, Loader2, XCircle } from 'lucide-react';
import { useId, type ReactNode } from 'react';

export function Spinner({ label }: { label?: string }): JSX.Element {
  return (
    <div className="flex items-center gap-2 text-sm text-tinta-medio" role="status">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      <span>{label ?? 'Carregando…'}</span>
    </div>
  );
}

type Tom = 'ok' | 'alerta' | 'erro' | 'info';

const TONS: Record<
  Tom,
  { fundo: string; texto: string; borda: string; faixa: string; Icone: typeof Info }
> = {
  ok: {
    fundo: 'bg-estado-okFundo',
    texto: 'text-estado-ok',
    borda: 'border-estado-ok/25',
    faixa: 'bg-estado-ok',
    Icone: CheckCircle2,
  },
  alerta: {
    fundo: 'bg-estado-alertaFundo',
    texto: 'text-estado-alerta',
    borda: 'border-estado-alerta/25',
    faixa: 'bg-estado-alerta',
    Icone: AlertTriangle,
  },
  erro: {
    fundo: 'bg-estado-erroFundo',
    texto: 'text-estado-erro',
    borda: 'border-estado-erro/25',
    faixa: 'bg-estado-erro',
    Icone: XCircle,
  },
  info: {
    fundo: 'bg-estado-infoFundo',
    texto: 'text-estado-info',
    borda: 'border-estado-info/25',
    faixa: 'bg-estado-info',
    Icone: Info,
  },
};

export function Aviso({
  tom = 'info',
  titulo,
  children,
}: {
  tom?: Tom;
  titulo?: string;
  children: ReactNode;
}): JSX.Element {
  const { fundo, texto, borda, Icone } = TONS[tom];
  return (
    <div
      className={`flex gap-3 rounded-xl border ${borda} ${fundo} px-4 py-3 shadow-sm`}
      role="status"
    >
      <Icone className={`mt-0.5 h-4 w-4 shrink-0 ${texto}`} aria-hidden="true" />
      <div className="min-w-0 text-sm">
        {titulo ? <p className={`font-semibold ${texto}`}>{titulo}</p> : null}
        <div className="text-tinta-escuro">{children}</div>
      </div>
    </div>
  );
}

/** Etiqueta de estado. O significado nunca depende só da cor — sempre há texto. */
export function Etiqueta({ tom, children }: { tom: Tom; children: ReactNode }): JSX.Element {
  const { fundo, texto } = TONS[tom];
  return <span className={`etiqueta ${fundo} ${texto}`}>{children}</span>;
}

export function Indicador({
  rotulo,
  valor,
  detalhe,
  destaque,
}: {
  rotulo: string;
  valor: ReactNode;
  detalhe?: string;
  destaque?: Tom;
}): JSX.Element {
  const cor = destaque ? TONS[destaque].texto : 'text-tinta-preto';
  return (
    <div className="cartao relative min-h-[126px] overflow-hidden px-5 py-4">
      <span
        className={`absolute inset-y-0 left-0 w-1 ${destaque ? TONS[destaque].faixa : 'bg-tinta-preto'}`}
      />
      <p className="text-2xs font-bold uppercase tracking-[0.1em] text-tinta-medio">{rotulo}</p>
      <p
        className={`mt-1.5 font-display text-3xl font-semibold tabular-nums tracking-tight ${cor}`}
      >
        {valor}
      </p>
      {detalhe ? <p className="mt-1 text-xs leading-relaxed text-tinta-medio">{detalhe}</p> : null}
    </div>
  );
}

export function Secao({
  titulo,
  descricao,
  acoes,
  children,
}: {
  titulo: string;
  descricao?: string;
  acoes?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  // O título nomeia a seção para leitores de tela.
  const idTitulo = useId();
  return (
    <section className="cartao overflow-hidden" aria-labelledby={idTitulo}>
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-superficie-borda bg-superficie-sutil/35 px-5 py-4">
        <div>
          <h2 id={idTitulo} className="font-display text-base font-semibold text-tinta-preto">
            {titulo}
          </h2>
          {descricao ? <p className="mt-0.5 text-xs text-tinta-medio">{descricao}</p> : null}
        </div>
        {acoes ? <div className="flex flex-wrap items-center gap-2">{acoes}</div> : null}
      </header>
      {children}
    </section>
  );
}

export function Vazio({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="px-5 py-10 text-center">
      <span className="mx-auto mb-3 block h-1 w-10 rounded-full bg-superficie-bordaForte" />
      <p className="text-sm text-tinta-medio">{children}</p>
    </div>
  );
}
